<template>
  <div class="login-container">
<transition name="fade">
  <main id="main-content" aria-label="Zone principale de la page de connexion">
<!-- Juste au-dessus du <form> -->
  <aside
  v-if="error && error.toLowerCase().includes('confirmer votre email')"
  class="alert-confirm-email"
  role="status"
  aria-live="polite"
>
  <i class="fas fa-envelope-open-text"></i>
  <div>
    <strong>📧 Action requise&nbsp;:</strong>
    <div>
      Pour accéder à votre compte, vous devez d’abord <b>confirmer votre adresse email</b>.<br>
      <span class="d-block my-2">Consultez votre boîte mail (y compris les spams).</span>
      <button
        class="btn btn-link p-0 text-decoration-underline text-primary"
        @click="resendConfirmationEmail"
        type="button"
        aria-label="Renvoyer l’email de confirmation"
        :disabled="isLoading"
      >
        Renvoyer l’email de confirmation
      </button>
      <span v-if="confirmationSent" class="text-success ms-2">✔️ Email renvoyé !</span>
    </div>
  </div>
</aside>

<h1 id="login-title" class="h2 mb-2 lesson-title">Connexion à votre compte</h1>
          <p class="text-muted mb-0">
           
            <span class="d-block text-center">Merci de saisir votre email et votre mot de passe&nbsp;:</span>
          </p>
 <form
  @submit.prevent="login"
  class="login-form"
  name="login"
  method="POST"
  data-netlify="true"
  aria-label="Formulaire de connexion"
  aria-describedby="login-form-info"
  role="form"
>

      <!-- 📧 Email -->
      <div class="form-group">
        <label for="email">{{ translatedLabels.email }}</label>
      <input
  id="email"
  class="form-control"
  v-model.trim="email"
  type="email"
  required
  :placeholder="translatedPlaceholders.email"
  :aria-label="translatedLabels.email"
  autocomplete="email"
  aria-required="true"
  :aria-invalid="!email"
/>

      </div>

      <!-- 🔒 Mot de passe -->
      <div class="form-group">
        <label for="password">{{ translatedLabels.password }}</label>
        <div class="password-wrapper">
       <input
  id="password"
  class="form-control"
  v-model="password"
  :type="showPassword ? 'text' : 'password'"
  required
  :placeholder="translatedPlaceholders.password"
  :aria-label="translatedLabels.password"
  autocomplete="current-password"
  aria-required="true"
  :aria-invalid="!password"
/>

          <button
            type="button"
            class="toggle-password"
            @click="togglePassword"
         :aria-label="showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'"

          >
      <i
  :class="['my-icon', showPassword ? 'fas fa-eye-slash text-danger' : 'fas fa-eye text-success']"
></i>

            
            
          </button>
        </div>
      </div>

      <!-- 🔘 Bouton de connexion -->
      <div class="form-group">
       <button
  type="submit"
  class="btn btn-success btn-lg w-100"
  :disabled="isLoading"
  aria-label="Se connecter à votre compte"
>
  {{ isLoading ? translatedLabels.loading : translatedLabels.submit }}
</button>

      </div>
<div class="sr-only btn btn-success" aria-live="assertive">
  <!-- Messages dynamiques d'erreur -->
</div>

      <!-- ⚠️ Message d'erreur -->
      <div v-if="error" class="error" role="alert">
        {{ error }}
      </div>

      <!-- 🔗 Liens de navigation -->
      <div class="form-links">
        <NuxtLink to="/register">{{ translatedLinks.register }}</NuxtLink> |
        <NuxtLink to="/forgot-password">{{
          translatedLinks.forgotPassword
        }}</NuxtLink>
      </div>
    </form>

  </main>

</transition>



  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useNuxtApp } from "#imports";
import { useRouter } from "vue-router";
import { useAuthStore } from "~/stores/authStore";
import * as Toastification from "vue-toastification";
const toast = Toastification.useToast();
const { $translate } = useNuxtApp();
import { useCartStore } from "~/stores/cartStore";
const cartStore = useCartStore();

const email = ref("");
const password = ref("");
const showPassword = ref(false);
const isLoading = ref(false);
const error = ref("");
const router = useRouter();
const authStore = useAuthStore();

// 🏷️ **Traductions dynamiques**
const translatedLabels = ref({
  email: "Email",
  password: "Mot de passe",
  submit: "Connexion",
  loading: "Connexion en cours...",
});
const translatedPlaceholders = ref({
  email: "Entrez votre email",
  password: "Entrez votre mot de passe",
});
const translatedErrors = ref({
  invalidEmail: "⚠️ Email invalide.",
  shortPassword: "⚠️ Le mot de passe doit contenir au moins 6 caractères.",
  loginFailed: "❌ Email ou mot de passe incorrect.",
});
const translatedLinks = ref({
  register: "Créer un compte",
  forgotPassword: "Mot de passe oublié ?",
});

// ✅ Chargement des traductions au montage
onMounted(async () => {
  const targetLang = navigator.language.split("-")[0] || "fr";

  translatedLabels.value = {
    email: await $translate("Email", "fr", targetLang),
    password: await $translate("Mot de passe", "fr", targetLang),
    submit: await $translate("Connexion", "fr", targetLang),
    loading: await $translate("Connexion en cours...", "fr", targetLang),
  };

  translatedPlaceholders.value = {
    email: await $translate("Entrez votre email", "fr", targetLang),
    password: await $translate("Entrez votre mot de passe", "fr", targetLang),
  };

  translatedErrors.value = {
    invalidEmail: await $translate("⚠️ Email invalide.", "fr", targetLang),
    shortPassword: await $translate(
      "⚠️ Le mot de passe doit contenir au moins 6 caractères.",
      "fr",
      targetLang
    ),
    loginFailed: await $translate(
      "❌ Email ou mot de passe incorrect.",
      "fr",
      targetLang
    ),
  };

  translatedLinks.value = {
    register: await $translate("Créer un compte", "fr", targetLang),
    forgotPassword: await $translate("Mot de passe oublié ?", "fr", targetLang),
  };
});

// ✅ **Validation stricte avant soumission**
const validateInputs = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.value.trim())) {
    error.value = translatedErrors.value.invalidEmail;
    return false;
  }
  if (password.value.length < 6) {
    error.value = translatedErrors.value.shortPassword;
    return false;
  }
  return true;
};
const confirmationSent = ref(false);

const resendConfirmationEmail = async () => {
  isLoading.value = true;
  confirmationSent.value = false;
  try {
    // On imagine une API POST /api/auth/resend-confirmation
    await $fetch("/api/auth/resend-confirmation", {
      method: "POST",
      body: { email: email.value.trim() },
    });
    confirmationSent.value = true;
    toast.success("Un nouvel email de confirmation a été envoyé !");
  } catch (e) {
    toast.error("Impossible d’envoyer le mail. Contactez le support si besoin.");
  } finally {
    isLoading.value = false;
  }
};

// 🔒 **Gestion de la connexion sécurisée**
/*const login = async () => {
  if (!validateInputs()) return;
  isLoading.value = true;
  error.value = "";

  try {
    await authStore.login({
      email: email.value.trim(),
      password: password.value,
    });
    toast.success("✅ Connexion réussie ! Redirection en cours...");

    // 🔄 **Redirection selon le rôle**
    const roles = authStore.profile.roles || [];
    const roleRoutes = {
      admin: "/admin",
      contributor: "/contributor",
      instructor: "/instructor",
      student: "/student",
      blogger: "/blogger",
    };
    const route = Object.keys(roleRoutes).find((role) => roles.includes(role));
    router.push(route ? roleRoutes[route] : "/user");
  } catch (err) {
    error.value = err.message || translatedErrors.value.loginFailed;
    toast.error(error.value);
  } finally {
    isLoading.value = false;
  }
};*/
// 🔒 Gestion de la connexion sécurisée
const login = async () => {
  if (!validateInputs()) return;
  isLoading.value = true;
  error.value = "";

  try {
    await authStore.login({
      email: email.value.trim(),
      password: password.value,
    });
    toast.success("✅ Connexion réussie ! Redirection en cours...");

    // 🔄 Redirection vers la page de profil
    const raw = authStore.profile.username || "";
    const usernameSlug = raw
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    await router.push(`/profile/${usernameSlug}`);
  } catch (err) {
    error.value = err.message || translatedErrors.value.loginFailed;
    toast.error(error.value);
  } finally {
    isLoading.value = false;
  }
};

// 👁️ **Afficher/Masquer le mot de passe**
const togglePassword = () => {
  showPassword.value = !showPassword.value;
};
</script>

<style scoped>
/* ✅ Container principal */

.login-container {
  max-width: 700px; /* Uniformisé avec Register.vue */
  margin: 40px auto;
  padding: 30px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
 /* Titre de la leçon */
 .lesson-title {
  color: #ff4500;
  margin-bottom: 20px;
  margin-top: 10px;
  text-align: center;
  font-size: 1.2rem;

}
button.btn-primary {
  transition: background-color 0.3s ease, transform 0.2s ease;
}
/* ✅ Formulaire */
.login-form {
  display: flex;
  flex-direction: column;
}

.form-group {
  margin-bottom: 15px;
}

label {
  font-weight: bold;
  margin-bottom: 5px;
  display: block;
}

input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s;
}

input:focus {
  border-color: #ff4500;
  outline: none;
}

/* ✅ Bouton pour afficher/masquer le mot de passe */
.password-wrapper {
  position: relative;
}

.toggle-password {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
}

.my-icon {
  margin-right: 8px;
  margin-left: 8px;
   color: #007bff;
  font-size: 1.1rem;
   vertical-align: middle;

}

/* ✅ Bouton principal */

.btn-primary {
  background-color: #007bff; /* Nouveau bleu */
  color: white;
  font-size: 1rem;
  padding: 12px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.2s ease;
}

.btn-primary:hover {
  background-color: #0069d9;
  box-shadow: 0 4px 10px rgba(0, 123, 255, 0.3);
  transform: scale(1.05); /* Agrandissement de 5% */
}

/* Facultatif : retour à l'état normal quand désactivé */
.btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  transform: none;
}


/* ✅ Message d'erreur */
.error {
  color: red;
  margin-top: 10px;
  text-align: center;
}

/* ✅ Liens supplémentaires */
.form-links {
  text-align: center;
  margin-top: 10px;
}

.form-links a {
  color: #ff4500;
  text-decoration: none;
}

.form-links a:hover {
  text-decoration: underline;
}
/* Peut être dans <style scoped> ou global, selon où tu veux réutiliser ce design */

.alert-confirm-email {
  background: linear-gradient(90deg, #fffbe5 70%, #ffe4a1 100%);
  border: 1.5px solid #ffe4a1;
  border-radius: 1rem;
  padding: 1.25rem 1rem 1.25rem 1.5rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  font-size: 1.06rem;
  box-shadow: 0 2px 8px rgba(255,212,104,0.09);
  min-height: 72px;
  position: relative;
  transition: box-shadow 0.2s;
}

.alert-confirm-email .fa-envelope-open-text {
  font-size: 2.2rem;
  color: #ffc107;
  flex-shrink: 0;
  margin-top: 0.12em;
  margin-right: 0.8em;
}

.alert-confirm-email strong {
  display: block;
  font-size: 1.12em;
  color: #7a4b00;
}

.alert-confirm-email button.btn-link {
  color: #007bff;
  text-decoration: underline;
  font-weight: 500;
  padding-left: 0;
  background: none !important;
  border: none;
  margin: 0;
  cursor: pointer;
  font-size: 1em;
}
.alert-confirm-email button.btn-link:disabled {
  color: #aaa !important;
  cursor: not-allowed;
}

@media (max-width: 600px) {
  .alert-confirm-email {
    flex-direction: column;
    padding: 1.2rem 0.8rem;
    font-size: 1em;
    min-height: 0;
  }
  .alert-confirm-email .fa-envelope-open-text {
    margin-bottom: 0.6em;
    margin-right: 0;
  }
}

/* ✅ Responsivité */
/*@media (max-width: 768px) {
  .login-container {
    max-width: 90%;
    padding: 20px;
  }

  .btn-primary {
    font-size: 0.9rem;
    padding: 10px;
  }

  input {
    font-size: 0.9rem;
  }

  .my-icon {
    font-size: 1rem;
  }
}*/
</style>

